// Powerlete mega menu — accessibility + touch enhancement.
// The open/close visual is CSS (:hover / :focus-within / .is-open), so the menu
// works with no JS. This layer adds: aria-expanded sync, Escape-to-dismiss (which
// keeps focus on the trigger per WCAG 2.2 SC 1.4.13), and tap-to-open on touch
// (coarse pointers have no hover, so the first tap opens the panel and the second
// follows the link).
function initMega(root) {
  // Wire each menu once. Guards against double-binding when both the initial pass and a
  // shopify:section:load re-init reach the same element (header re-render in the theme editor).
  if (root.dataset.pwWired) return;
  root.dataset.pwWired = '1';
  const items = Array.from(root.querySelectorAll('.pw-mega__item'));
  const coarse = window.matchMedia('(hover: none)');

  const setExpanded = (item, open) => {
    const link = item.querySelector('.pw-mega__link');
    if (link) link.setAttribute('aria-expanded', open ? 'true' : 'false');
  };

  const closeAll = (except) => {
    items.forEach((it) => {
      if (it !== except) {
        it.classList.remove('is-open');
        setExpanded(it, false);
      }
    });
  };

  items.forEach((item) => {
    const link = item.querySelector('.pw-mega__link');
    const panel = item.querySelector('.pw-mega__panel');
    if (!link) return;

    item.addEventListener('pointerenter', (e) => {
      if (e.pointerType === 'touch') return;
      item.classList.remove('is-dismissed');
      // Enforce a single open panel. Moving the mouse onto a new top-level item
      // makes it the active menu, so close any sibling still held open by a tap
      // (.is-open) or by focus landing inside its panel (:focus-within) — otherwise
      // the previous panel stays visible underneath the new one.
      closeAll(item);
      const active = document.activeElement;
      const activeItem = active && active.closest ? active.closest('.pw-mega__item') : null;
      if (activeItem && activeItem !== item && typeof active.blur === 'function') active.blur();
      setExpanded(item, true);
    });
    item.addEventListener('pointerleave', (e) => {
      if (e.pointerType === 'touch') return;
      setExpanded(item, false);
    });
    item.addEventListener('focusin', () => {
      item.classList.remove('is-dismissed');
      setExpanded(item, true);
    });
    item.addEventListener('focusout', (e) => {
      if (!item.contains(e.relatedTarget)) {
        item.classList.remove('is-dismissed');
        setExpanded(item, false);
      }
    });

    // Touch: first tap opens the panel (no hover available); a second tap on the
    // same label follows the link to the category page.
    link.addEventListener('click', (e) => {
      if (!coarse.matches || !panel) return;
      if (!item.classList.contains('is-open')) {
        e.preventDefault();
        closeAll(item);
        item.classList.add('is-open');
        setExpanded(item, true);
      }
    });
  });

  // Escape dismisses the open panel but keeps focus on the trigger.
  //
  // ORDER MATTERS: focus() FIRST, then dismiss. focus() is synchronous and focusin
  // bubbles, so moving focus to the link (a descendant of the same .pw-mega__item)
  // fires the focusin handler above, which strips is-dismissed and sets
  // aria-expanded=true again. With the dismiss written first, that handler ran
  // immediately AFTER it and undid it -- and since the CSS keeps the panel visible on
  // :focus-within (blocks/powerlete-mega-menu.liquid:98) with only .is-dismissed
  // overriding it (:115), the panel snapped straight back open. This contradicted this
  // file's own header comment claiming SC 1.4.13 Dismissible.
  //
  // Doing focus() first means focusin fires (un-dismissing), and THEN the dismiss lands
  // as the final state. Focus does not move again afterwards, so focusin cannot re-fire.
  // Only reachable when focus is already inside the panel; the hover-then-Escape path
  // was never affected.
  root.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const focused = document.activeElement;
    const item = focused && focused.closest ? focused.closest('.pw-mega__item') : null;
    if (!item) return;
    const link = item.querySelector('.pw-mega__link');
    if (link && link.focus) link.focus();
    item.classList.add('is-dismissed');
    item.classList.remove('is-open');
    setExpanded(item, false);
  });

  // Tap/click outside closes any tap-opened panel. Keep a handle so the document-level listener
  // can be removed when the section is torn down in the theme editor; the per-item listeners die
  // with their elements, but this one outlives the header re-render and would otherwise stack.
  const onDocClick = (e) => {
    if (!root.contains(e.target)) closeAll();
  };
  document.addEventListener('click', onDocClick);
  root._pwMegaDocClick = onDocClick;
}

document.querySelectorAll('.pw-mega').forEach(initMega);

// Re-init menus in a section loaded/reloaded in the theme editor (header re-render). The pwWired
// guard in initMega makes re-initialising already-wired menus a no-op.
document.addEventListener('shopify:section:load', (e) => {
  e.target.querySelectorAll('.pw-mega').forEach(initMega);
});

// Drop the document-level outside-click listener when the header section is removed/reloaded in
// the theme editor, so it doesn't accumulate against a detached menu on every re-render.
document.addEventListener('shopify:section:unload', (e) => {
  e.target.querySelectorAll('.pw-mega').forEach((root) => {
    if (root._pwMegaDocClick) {
      document.removeEventListener('click', root._pwMegaDocClick);
      root._pwMegaDocClick = null;
    }
  });
});
